'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ConsentCategory, ConsentSource, ConsentState } from './types';
import { readConsentCookie, writeConsentCookie } from './consent-cookie';

/**
 * Consent payload schema version (cookie format). Bump when ConsentState
 * structure changes in a breaking way.
 */
const CONSENT_VERSION = '1.1.0';

/**
 * Policy version — bump when the privacy / cookie policy changes materially
 * (per WP29 Opinion 15/2011 §III.A.4 + GDPR Art. 7(1)). Stored on every
 * consent record so we can detect stale consent and trigger re-prompt.
 *
 * Sourced from `NEXT_PUBLIC_COOKIE_CONSENT_POLICY_VERSION` env at build
 * time. Falls back to '1.0.0' for repos that haven't set it yet.
 *
 * Implementation note: read via globalThis so the package keeps a zero-
 * dependency stance on `@types/node`. Next.js inlines NEXT_PUBLIC_* at
 * build time so this access pattern still gets the build-time value.
 */
function readPolicyVersionFromEnv(): string {
  const g = globalThis as { process?: { env?: Record<string, string | undefined> } };
  const env = g.process?.env;
  if (env) {
    return (
      env.NEXT_PUBLIC_COOKIE_CONSENT_POLICY_VERSION ||
      env.COOKIE_CONSENT_POLICY_VERSION ||
      '1.0.0'
    );
  }
  return '1.0.0';
}
const POLICY_VERSION = readPolicyVersionFromEnv();

interface ConsentContextValue {
  consent: ConsentState | null;
  setConsent: (state: ConsentState) => Promise<void>;
  hasConsent: (category: ConsentCategory) => boolean;
  withdrawConsent: () => Promise<void>;
  /**
   * Whether the current session is operating under a Global Privacy Control
   * (GPC) signal. When true the banner SHOULD auto-dismiss with
   * default-deny state per CCPA §1798.135 / CPRA §1798.185(a)(20).
   */
  gpcEnabled: boolean;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') return generateSessionId();
  const key = '_usid';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = generateSessionId();
    sessionStorage.setItem(key, id);
  }
  return id;
}

/**
 * Detect the Global Privacy Control signal exposed by privacy-respecting
 * browsers (DuckDuckGo, Firefox, Brave, Edge optional flag). Required by
 * California Final Regs (FSOR 2023-03) + CPRA + 7 other state laws.
 *
 * Returns false in SSR and when navigator does not expose the property.
 */
function detectGPC(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!('globalPrivacyControl' in navigator)) return false;
  // The property is exposed as a boolean true (or undefined / false otherwise).
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

/**
 * Build a default-deny consent state for GPC-respected sessions. All
 * non-essential categories are denied. policy_version + source are tagged
 * so the audit log can prove the deny was driven by GPC, not user click.
 */
function buildGpcDenyState(): ConsentState {
  return {
    version: CONSENT_VERSION,
    policy_version: POLICY_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    },
    source: 'gpc',
  };
}

export function ConsentProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [consent, setConsentState] = useState<ConsentState | null>(null);
  const [gpcEnabled, setGpcEnabled] = useState<boolean>(false);

  useEffect(() => {
    const gpc = detectGPC();
    setGpcEnabled(gpc);

    const stored = readConsentCookie();

    // Re-prompt trigger: stored consent exists but version OR policy_version
    // differs from the current values. Treat as no-consent and let the
    // banner re-collect.
    const isFresh =
      stored !== null &&
      stored.version === CONSENT_VERSION &&
      stored.policy_version === POLICY_VERSION;

    if (gpc && !isFresh) {
      // W2.2 Finding 3 (CCPA §1798.135 / CPRA): GPC=true short-circuits to
      // default-deny without showing the banner. Persist immediately so
      // downstream gates (Sentry, analytics) see denied state on first paint.
      const denyState = buildGpcDenyState();
      writeConsentCookie(denyState);
      setConsentState(denyState);
      // Fire-and-forget audit log so the deny is demonstrable under DPA.
      void fetch('/api/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: denyState, sessionId: getSessionId() }),
      }).catch(() => {
        // best-effort — cookie is already written locally
      });
      return;
    }

    if (isFresh) {
      setConsentState(stored);
    }
    // else: leave consent null so the banner mounts and prompts the user.
  }, []);

  const setConsent = useCallback(async (state: ConsentState): Promise<void> => {
    // Ensure every persisted record carries the current policy_version even
    // if a caller forgot to set it. Default source to 'user' for explicit
    // clicks — withdrawal + GPC paths set their own source explicitly.
    const stamped: ConsentState = {
      ...state,
      policy_version: state.policy_version || POLICY_VERSION,
      source: state.source ?? 'user',
    };
    writeConsentCookie(stamped);
    setConsentState(stamped);
    try {
      await fetch('/api/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: stamped, sessionId: getSessionId() }),
      });
    } catch {
      // best-effort — cookie is already written locally
    }
  }, []);

  const hasConsent = useCallback(
    (category: ConsentCategory): boolean => {
      if (!consent) return false;
      return consent.categories[category] === true;
    },
    [consent],
  );

  const withdrawConsent = useCallback(async (): Promise<void> => {
    const withdrawn: ConsentState = {
      version: CONSENT_VERSION,
      policy_version: POLICY_VERSION,
      timestamp: new Date().toISOString(),
      categories: {
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false,
      },
      source: 'withdrawn' satisfies ConsentSource,
    };
    await setConsent(withdrawn);
  }, [setConsent]);

  return React.createElement(
    ConsentContext.Provider,
    { value: { consent, setConsent, hasConsent, withdrawConsent, gpcEnabled } },
    children,
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return ctx;
}

/**
 * Convenience hook for callers that only care about the GPC signal (e.g. a
 * pre-banner gate that decides whether to show the banner at all). Returns
 * false when used outside a ConsentProvider — non-throwing so it can be
 * used in feature-detection paths without crashing the tree.
 */
export function usePrivacyControl(): { gpcEnabled: boolean } {
  const ctx = useContext(ConsentContext);
  return { gpcEnabled: ctx?.gpcEnabled ?? false };
}

export { CONSENT_VERSION, POLICY_VERSION };
